<script setup lang="ts">
import { useWindowWidth } from "@/stores/useWindowWidth";
import { storeToRefs } from "pinia";
import { ref } from "vue";
import { useModal } from "vue-final-modal";
import AdminDataExportDialog from "~/components/AdminDataExport/Dialog.client.vue";
import { useAnnotationTokenHighlight } from "~/stores/useAnnotationTokenHighlight";
import { useUserDocumentSettings } from "~/stores/useUserDocumentSettings";
import { RevisionAnnotation } from "~/types/RevisionAnnotation";

import { useChecklistControllerStore } from "~/composables/useChecklistControllerStore";

const props = defineProps({
  documentId: {
    type: String,
    required: true,
  },
  isCompanionMode: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(["onSubmitted"]);

const documentViewModeStore = useDocumentViewMode(props.documentId)();
const {
  isTeacherViewMode,
  isStudentViewMode,
  showDocumentAppBar,
  isConsolidatedMode,
  isReviewMode,
  isSubmitMode,
} = storeToRefs(documentViewModeStore);

documentViewModeStore.setIsCompanionMode(props.isCompanionMode);

const documentSelfAssessmentStore = useDocumentSelfAssessmentStore(
  props.documentId
);
const { isInitialized: isSelfAssessmentInitialized } = storeToRefs(
  documentSelfAssessmentStore
);

const useParagraphsStore = useDocumentRevisionParagraphsStore(props.documentId);

const docStore = useSubmittedDocumentStore(props.documentId);
const { revision, submittedDocument, isDocumentInitialized, isEditingMode } =
  storeToRefs(docStore);

const useAnnotationsStore = useDocumentAnnotationsStore(props.documentId);

const { allAnnotations } = storeToRefs(useAnnotationsStore);

const { wordChecklistCells } = storeToRefs(useParagraphsStore);

const { updateWindowWidth } = useWindowWidth();
const { updateWindowHeight } = useWindowHeight();

const documentSelectionStore = useDocumentSelectionStore(props.documentId);
const { setSelection, clearSelection } = documentSelectionStore;

const draggingAnnotationStore = useDraggingAnnotation();
const { isAnAnnotationBeingDragged } = storeToRefs(draggingAnnotationStore);

const onSelectionChanged = () => {
  if (isAnAnnotationBeingDragged.value) return;

  const selection = document.getSelection();

  if (
    (selection && selection.rangeCount == 0) ||
    selection?.isCollapsed == true
  ) {
    clearSelection();
    return;
  }

  if (selection && selection.rangeCount > 0) {
    // Check if the selection has a parent node closest with class "document-compare-container"
    if (
      selection.anchorNode?.parentElement?.closest(
        ".document-compare-container"
      )
    ) {
      return;
    }

    const startPosition = selection.anchorNode?.parentElement
      ?.closest(".paragraph-token")
      ?.getAttribute("data-token-position");
    const endPosition = selection.focusNode?.parentElement
      ?.closest(".paragraph-token")
      ?.getAttribute("data-token-position");

    if (startPosition && endPosition) {
      setSelection(parseInt(startPosition), parseInt(endPosition));
    }
  }
};

const checklistControllerStore = useChecklistControllerStore(props.documentId);

const documentChecklistStore = useDocumentChecklistStore(props.documentId);
const {
  isOSS,
  hasCorrectNumberOfBodyParagraphs,
  numExpectedBodyParagraphs,

  hasShownMissmatchMessage,
  isInitialized: isChecklistInitialized,
} = storeToRefs(documentChecklistStore);

const isInitialized = ref(false);
const initializeStores = async () => {
  if (isInitialized.value == true) return;

  useParagraphsStore.initialize(props.documentId, revision.value!.id!);
  useAnnotationsStore.initialize();
  useDocumentHeadersStore(props.documentId).initialize();
  isInitialized.value = true;
  await documentChecklistStore.initialize();

  await documentSelfAssessmentStore.initialize();

  checklistControllerStore.initializeController();
};

const onResize = () => {
  updateWindowWidth();
  updateWindowHeight();
};

onMounted(() => {
  window.addEventListener("resize", onResize);
  document.addEventListener("selectionchange", onSelectionChanged);
  onResize();
  initializeStores();
});

onUnmounted(() => {
  window.removeEventListener("resize", onResize);
  document.removeEventListener("selectionchange", onSelectionChanged);
});

const clearHighlightedAnnotation = () => {
  const { setHoverAnnotation } = useAnnotationTokenHighlight();
  setHoverAnnotation(undefined);
};

const annotationsWithComment = computed(() => {
  if (!allAnnotations.value) return [];

  return allAnnotations.value.filter(
    (annotation: RevisionAnnotation) => annotation.commentObject != null
  );
});

const appUserDataStore = useAppUserDataStore();
const { appUser, isUserSystemAdmin, isUserAdmin } = appUserDataStore;
const { isStudentAccount } = storeToRefs(appUserDataStore);

const userDocumentSettingsStore = useUserDocumentSettings();
const { toggleInlineAnnotations } = userDocumentSettingsStore;
const { allowInlineAnnotations } = storeToRefs(userDocumentSettingsStore);
allowInlineAnnotations.value = appUser?.showInlineAnnotations ?? false;

const { open, close } = useModal({
  component: AdminDataExportDialog,
  attrs: {
    onClose: () => {
      close();
    },
  },
});

const exportData = () => {
  open();
};

const { reviewProblem, evaluationTestId } = useRoute().query;

const tourBuilderStore = useTourBuilder();
const { isBuildingTour } = storeToRefs(tourBuilderStore);

const isSavingSample = ref(false);

const saveSample = async () => {
  isSavingSample.value = true;

  await $fetch(`/api/admin/demos/${props.documentId}/save-sample`, {
    method: "POST",
    headers: await useApiHeaders(),
  });

  isSavingSample.value = false;
};

const exportFormats = computed(() => {
  // Because this is a student only button, we're only going to support PDF epxorts for them
  return [ExportDocumentFormat.pdf];

  // if (isOSS.value == true) return [ExportDocumentFormat.docx];

  // if (
  //   revision.value?.fileObject != undefined &&
  //   revision.value.fileObject.mimeType.includes("pdf")
  // ) {
  //   return [ExportDocumentFormat.pdf];
  // }

  // return [ExportDocumentFormat.docx, ExportDocumentFormat.pdf];
});

const isStudentReviewEditing = computed(() => {
  if (submittedDocument.value == undefined) return false;

  return [
    DocumentState.draft,
    DocumentState.studentReview,
    DocumentState.editing,
    DocumentState.studentCompleted,
  ].includes(submittedDocument.value.state);
});

// Teachers open in-progress papers in student mode from Not Submitted.
const showLiveSubmissionWarning = computed(() =>
  isStudentViewMode.value &&
  !isStudentAccount.value &&
  !!submittedDocument.value?.studentId &&
  isStudentReviewEditing.value
);

const isStudentReviewMode = computed(() => {
  if (submittedDocument.value == undefined) return false;

  return [DocumentState.studentReview, DocumentState.studentCompleted].includes(
    submittedDocument.value.state
  );
});

const isStudentEditingMode = computed(() => {
  if (submittedDocument.value == undefined) return false;

  return [DocumentState.draft, DocumentState.editing].includes(
    submittedDocument.value.state
  );
});

const showSidePanel = computed(() => {
  if (isSubmitMode.value == true) {
    return false;
  }

  if (isReviewMode.value == true) {
    return false;
  }

  if (isTeacherViewMode.value != true) {
    return false;
  }

  return reviewProblem != "true" || evaluationTestId == undefined;
});

const showEditorPanel = computed(() => {
  if (isStudentReviewMode.value == true) {
    return false;
  }

  if (isTeacherViewMode.value == true) return false;

  if (isEditingMode.value != true) return false;

  if (isStudentReviewEditing.value != true) return false;

  return true;
});

watch([hasCorrectNumberOfBodyParagraphs, isChecklistInitialized], async () => {
  displayMessage();
});

const displayMessage = async () => {
  if (isChecklistInitialized.value != true) return;

  if (
    hasCorrectNumberOfBodyParagraphs.value == true ||
    hasShownMissmatchMessage.value == true
  ) {
    return;
  }

  if (submittedDocument.value?.state != DocumentState.studentReview) {
    return;
  }

  if (isTeacherViewMode.value == true) {
    // here is hwere I'll put the new thing Heidi is working on
  }

  if (isTeacherViewMode.value != true) {
    // Here is where I'll put the document message
    // await useBaseAlert(
    //   "This paper is incorrectly formatted. Look at the assignment and checklist to determine how many paragraphs you should submit.",
    //   StudentExplanationModal,
    //   true
    // );
  }
};

onMounted(() => {
  displayMessage();
  initializeClassroomStores();
});

watch(submittedDocument, () => {
  initializeClassroomStores();
});

const initializeClassroomStores = () => {
  if (submittedDocument.value == undefined) return;

  if (
    submittedDocument.value.classroomId != undefined &&
    submittedDocument.value.studentId != undefined
  ) {
    useInitializeClassroomStores(
      submittedDocument.value.classroomId,
      submittedDocument.value.studentId
    );
  }
};

const hasMediaObject = computed(() => {
  return (
    revision.value?.fileObject != undefined ||
    (revision.value?.fileObjects != undefined &&
      revision.value.fileObjects.length > 0)
  );
});

const subtitle = computed(() => {
  let parts = [] as string[];

  if (
    submittedDocument.value?.classroomName != undefined &&
    submittedDocument.value?.classroomName != ""
  ) {
    parts.push(submittedDocument.value?.classroomName);
  }

  if (
    submittedDocument.value?.assignmentName != undefined &&
    submittedDocument.value?.assignmentName != ""
  ) {
    parts.push(submittedDocument.value?.assignmentName);
  }

  if (
    submittedDocument.value?.studentName != undefined &&
    submittedDocument.value?.studentName != ""
  ) {
    parts.push(submittedDocument.value?.studentName);
  }

  return parts.join(" | ");
});

const originalFileUrl = computed(() => {
  const document = submittedDocument.value;
  if (document?.googleDriveFileId) {
    return `https://drive.google.com/file/d/${document.googleDriveFileId}/view`;
  }

  return document?.originalFileMediaHref;
});

const isGoogleDriveFile = computed(
  () => submittedDocument.value?.googleDriveFileId != undefined
);
</script>

<template>
  <div class="h-full flex flex-col min-h-0 overflow-hidden bg-surface">
    <slot name="app-bar" :submittedDocument="submittedDocument">
      <BaseAppBar v-if="showDocumentAppBar" class="fixed z-50">
        <template #leading>
          <span class="flex flex-row items-center h-full min-w-0 ml-2">
            <NuxtLink class="mr-3 shrink-0" to="/">
              <BaseIconButton :size="40">
                <icon name="material-symbols:home" size="40" />
              </BaseIconButton>
            </NuxtLink>
            <div class="flex flex-col min-w-0">
              <span id="document-title" class="text-h6 truncate">
                {{ submittedDocument?.name }}
              </span>
              <div v-if="subtitle != ''" class="text-body-2 text-medium truncate">
                {{ subtitle }}
              </div>
            </div>
          </span>
        </template>
        <template #actions>
          <DocumentStudentDraftDownloadButton
            v-if="
              isStudentViewMode &&
              !isCompanionMode &&
              Array.isArray(revision?.studentSubmissionOps)
            "
            :document-id="documentId"
          />
          <DocumentEditingHistoryButton
            v-if="
              revision?.id != undefined &&
              isTeacherViewMode != true &&
              (submittedDocument?.state == DocumentState.editing ||
                submittedDocument?.state == DocumentState.studentReview)
            "
            :submitted-document-id="documentId"
            :revision-id="revision?.id"
          />

          <ExportDocumentButton
            v-if="
              isTeacherViewMode != true &&
              submittedDocument?.state == DocumentState.graded
            "
            class="mr-2"
            :formats="exportFormats"
            color="secondary"
          />
          <span v-if="isTeacherViewMode || originalFileUrl" class="flex items-center gap-2">
            <a
              v-if="originalFileUrl && !isTeacherViewMode"
              :href="originalFileUrl"
              target="_blank"
              v-tooltip="isGoogleDriveFile ? 'Open in Google Drive' : 'Download Word Document'"
              rel="noopener noreferrer"
              class="shrink-0 inline-flex h-10 items-center justify-center gap-1 rounded px-2 text-white transition-colors"
              :class="isGoogleDriveFile ? 'bg-google-default hover:bg-google-shade' : 'bg-[#2b579a] hover:bg-[#22457a]'"
              :aria-label="isGoogleDriveFile ? 'Open in Google Drive' : 'Download Word document'"
            >
              <BaseIconGoogleFile v-if="isGoogleDriveFile" size="22" />
              <BaseIconWordFile v-else size="22" />
              <BaseIconExternalLink v-if="isGoogleDriveFile" size="17" />
              <BaseIconDownload v-else size="17" />
            </a>
            <NuxtLink v-if="isTeacherViewMode" to="/articles" target="_blank" aria-label="View Help Articles">
              <BaseIconButton tooltip="View Help Articles" :size="40">
                <icon name="material-symbols:help" size="28" />
              </BaseIconButton>
            </NuxtLink>
            <ReportProblemButton v-if="isTeacherViewMode" variant="icon" :icon-size="40" :is-consolidated="true" />
            <DocumentSavingStatusIndicator v-if="isTeacherViewMode" class="hidden md:flex" />
            <BasePopover v-if="isTeacherViewMode && isUserAdmin" align="end">
              <BaseIconButton tooltip="More options" :size="40" aria-label="More options">
                <icon name="material-symbols:more-vert" size="28" />
              </BaseIconButton>
              <template #content>
                <div
                  v-if="isUserSystemAdmin"
                  class="flex flex-row items-center p-1 h-[40px]"
                >
                  <BaseToggle v-model="isBuildingTour" />
                  <span class="ml-2"> Enable Tour Builder </span>
                </div>
                <BaseTextButton
                  v-if="isUserSystemAdmin"
                  class="h-[40px]"
                  :show-spinner="isSavingSample"
                  @click="saveSample"
                >
                  Save as Sample
                </BaseTextButton>
                <BaseTextButton
                  v-if="isUserSystemAdmin"
                  class="h-[40px]"
                  @click="exportData"
                >
                  Export Data
                </BaseTextButton>
              </template>
            </BasePopover>
          </span>
        </template>
      </BaseAppBar>
    </slot>
    <div
      id="document-scroll-container"
      class="flex flex-row h-full overflow-hidden min-h-0"
      :class="{
        'pt-[60px]':
          isConsolidatedMode != true &&
          isReviewMode != true &&
          isSubmitMode != true &&
          isCompanionMode != true,
      }"
      data-step-id="document-page-container"
      @click="clearHighlightedAnnotation"
      v-auto-animate
    >
      <div class="flex flex-row h-full min-h-0">
        <DocumentGuidedCorrectionsGate
          v-if="isInitialized && !isTeacherViewMode && (isStudentEditingMode || isStudentReviewMode)"
        >
          <DocumentStudentEditingContainer v-if="isStudentEditingMode" />
          <DocumentSelfAssessmentWrapper
            v-else
            @on-submitted="$emit('onSubmitted')"
          />
        </DocumentGuidedCorrectionsGate>
        <!-- <DocumentEditorReviewPanelContainer
          v-if="
            showEditorPanel &&
            submittedDocument?.classroomId &&
            submittedDocument?.studentId
          "
          :classroomId="submittedDocument.classroomId"
          :studentId="submittedDocument.studentId"
          :checklistId="submittedDocument.checklistId"
        /> -->
        <div class="h-full w-[6px] bg-background border-l border-r" />
      </div>

      <div
        id="document-container-wrapper"
        class="flex flex-col items-stretch max-w-[800px] w-full h-full mx-auto"
      >
        <div
          v-if="showLiveSubmissionWarning"
          role="status"
          class="mx-4 mt-2 flex shrink-0 items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-950"
        >
          <BaseIconAlert aria-hidden="true" class="mt-0.5 size-4 shrink-0 text-amber-700" />
          <div>
            <p class="text-sm font-bold">Live student submission</p>
            <p class="mt-0.5 text-sm leading-5">
              The student is still working on this paper. Please proceed with caution as any edits you make will change their work.
            </p>
          </div>
        </div>
        <div v-if="isTeacherViewMode" class="px-2">
          <DocumentToolbar
            class="mt-2 mx-4"
            :original-file-url="originalFileUrl"
            :is-google-drive-file="isGoogleDriveFile"
          />
        </div>
        <div
          id="document-container"
          ref="documentRef"
          class="grow min-h-0 w-full"
          :class="{
            ' mt-2 px-4 ': isConsolidatedMode != true,
          }"
        >
          <DocumentEditorContainer
            v-if="isSelfAssessmentInitialized && isEditingMode == true"
          />
          <div
            v-if="isEditingMode != true"
            id="paragraphs-wrapper"
            class="h-full overflow-auto relative w-full bg-surface m-0 px-2"
          >
            <DocumentCompanionExportButtons v-if="isCompanionMode == true" />
            <div
              class="border shadow rounded-tl rounded-tr w-full min-h-full flex flex-col"
              :class="{
                'p-12': submittedDocument?.state != DocumentState.editing,
                'p-4': submittedDocument?.state == DocumentState.editing,
              }"
              v-auto-animate
            >
              <DocumentLoadingContainer
                v-if="!isInitialized"
                :documentId="documentId"
              />
              <div v-if="isOSS && isInitialized" class="mb-0 text-h5 font-bold">
                <span class="mr-2"> Name: </span>
                {{ submittedDocument?.enteredName }}
              </div>
              <DocumentWordChecklistContainer
                v-if="
                  isInitialized &&
                  revision &&
                  wordChecklistCells &&
                  wordChecklistCells.length > 0
                "
                :word-checklist-cells="wordChecklistCells"
              />
              <DocumentParagraphsContainer v-if="hasMediaObject != true" />
              <DocumentFilePreview v-if="hasMediaObject" />
              <DocumentMergeParagraphsContainer v-if="isTeacherViewMode" />
              <DocumentFixAssignmentContainer v-if="isTeacherViewMode" />
            </div>
          </div>
          <div v-if="allAnnotations">
            <DocumentAnnotationTooltipContainer
              v-for="annotation in annotationsWithComment"
              :key="annotation.id"
              :annotation="annotation"
            />
          </div>

          <AnnotationFormContainer v-if="isInitialized && isTeacherViewMode" />
          <DocumentGeneralFeedbackFloatingForm
            v-if="isInitialized && isTeacherViewMode"
          />
        </div>

        <DocumentQuickActionsContainer
          v-if="isInitialized && isTeacherViewMode"
        />
        <DocumentAddStyleContainer v-if="isInitialized" />
      </div>
      <div
        v-if="
          isConsolidatedMode != true &&
          isCompanionMode != true &&
          (showSidePanel == true || showEditorPanel == true)
        "
        class="w-[6px] h-full bg-background border-l border-r"
      />
      <DocumentPanelContainer v-if="showSidePanel" />

      <DocumentProblemReviewContainer
        v-if="
          isUserSystemAdmin == true &&
          reviewProblem == 'true' &&
          evaluationTestId
        "
        class="w-[600px] my-2 mr-4 shrink-0"
        :evaluation-test-id="evaluationTestId as string"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
